// Shared dismiss-and-reset behaviour for KjpTable.vue's warnings banner and
// KjpChart.vue's absent-series notice.
//
// Both banners are transient (never persisted to localStorage). The same
// component instance is reused across every scope's inner tab — switching
// tabs only patches the `model` prop, it does not remount KjpTable/KjpChart
// — so a plain "reset on any model change" watcher cannot tell "the user
// came back to a scope they already dismissed" apart from "this is
// genuinely new data": both look identical, a changed `model` prop. That
// was the previous implementation here, and it did not actually give each
// scope an independent dismissal: dismiss R01's banner, switch to R02 (a
// different model, correctly resets), switch back to R01 (the model prop
// changes again, even though it's reverting to a value already seen) and
// the watcher fired again, resurrecting the banner the user had already
// dismissed.
//
// Keying the dismissal by scope, and requiring the exact model object that
// was dismissed, is what actually makes each scope's notice dismiss
// independently: switching back to a scope's tab restores whatever it was
// left at, while a fresh Apply always builds new model objects (even for a
// scope whose figures came back unchanged) and so always re-surfaces the
// notice, matching the original intent.
//
// Known limitation this does NOT cover: the Data/Chart view toggle in
// KjpSummary.vue alternates between the kjp-chart and kjp-table tags with
// v-if/v-else-if, so switching between them destroys and recreates
// whichever one is hidden — that resets this dismissal (and KjpTable's own
// showAllMonths) regardless of scope, every time. Fixing that would mean
// the two views stop being fully independent components (e.g. keep-alive,
// or lifting this state up into KjpSummary), which is a larger change than
// this shared mixin should make unilaterally; left as a documented gap
// rather than silently papered over.
export default {
  data() {
    return {
      // Scope -> the exact model object whose notice was dismissed for it.
      // A scope missing from this map, or present but pointing at a
      // *different* model object than the one currently shown, reads as
      // not dismissed.
      dismissedFor: {},
    };
  },
  computed: {
    dismissed: {
      get() {
        const scope = this.model && this.model.scope;

        return !!scope && this.dismissedFor[scope] === this.model;
      },
      set(value) {
        const scope = this.model && this.model.scope;
        if (!scope) return;

        this.$set(this.dismissedFor, scope, value ? this.model : null);
      },
    },
  },
};
